import { antdDrawerV5 } from '@ebay/nice-modal-react'
import { t } from '@lingui/core/macro'
import { SectionFlow } from '@pkg/form/index'
import type { SectionFlowMode, SectionFlowReturn } from '@pkg/form/useSectionFlow'
import { ActionButton } from '@pkg/ui/button'
import { useDrawerCloseGuard } from '@pkg/ui/drawer'
import type { TypedModalHandler } from '@pkg/ui/modal'
import { Button, Drawer, type DrawerProps, Result, Space } from 'antd'
import type { ReactNode } from 'react'
import { FaChevronLeft, FaTrash } from 'react-icons/fa6'

function ConfirmClose({ onDiscard, onCancel }: { onDiscard: () => void; onCancel: () => void }) {
  return (
    <Result
      status="warning"
      title={t`Änderungen verwerfen?`}
      subTitle={t`Es gibt ungespeicherte Änderungen. Möchtest du diese wirklich verwerfen?`}
      extra={
        <Space>
          <ActionButton icon={<FaChevronLeft />} onClick={onCancel}>{t`Weiter bearbeiten`}</ActionButton>
          <ActionButton color="danger" icon={<FaTrash />} onClick={onDiscard}>{t`Verwerfen`}</ActionButton>
        </Space>
      }
    />
  )
}

interface EditDrawerControls {
  requestClose: () => void
}

interface EditDrawerProps extends Omit<DrawerProps, 'extra' | 'title' | 'onClose'> {
  modal: TypedModalHandler<never>
  flow: SectionFlowReturn
  isDirty?: boolean
  error?: Error | null
  /** Fixed drawer heading in `sequential` mode. Defaults to a generic label. */
  title?: ReactNode
  titleFor?: { [key in SectionFlowMode]?: ReactNode }
  onClose?: () => void
  onDiscard?: () => void
  extra?: ReactNode | ((controls: EditDrawerControls) => ReactNode)
  children: ReactNode
}

export function EditDrawer({
  modal,
  flow,
  error,
  children,
  extra,
  isDirty,
  title,
  titleFor,
  onClose,
  onDiscard,
  ...drawerProps
}: EditDrawerProps) {
  const { showConfirm, handleClose, handleDiscard, handleCancelClose } = useDrawerCloseGuard({
    isDirty: !!isDirty,
    onClose: onClose ?? (() => modal.rejectRemove()),
    onDiscard: onDiscard ?? (() => void modal.hide()),
  })
  const resolvedExtra = showConfirm ? null : typeof extra === 'function' ? extra({ requestClose: handleClose }) : extra

  return (
    <Drawer
      size={flow.mode === 'sequential' ? '90%' : '70%'}
      {...drawerProps}
      {...antdDrawerV5(modal)}
      onClose={handleClose}
      keyboard
      mask={{ closable: false }}
      data-testid="edit-drawer"
      title={
        titleFor && titleFor[flow.mode] !== undefined
          ? titleFor[flow.mode]
          : flow.mode === 'sequential'
            ? (title ?? t`Hinzufügen`)
            : flow.sections[flow.currentIndex]?.title
      }
      extra={resolvedExtra}
    >
      {error && (
        <Result
          status="error"
          title={t`Fehler beim Laden`}
          subTitle={error.message}
          extra={
            <Button
              onClick={() => {
                modal.reject(error)
                modal.remove()
              }}
            >
              {t`Schließen`}
            </Button>
          }
        />
      )}
      {showConfirm && <ConfirmClose onDiscard={handleDiscard} onCancel={handleCancelClose} />}
      <div style={{ display: showConfirm || error ? 'none' : undefined }}>
        {flow.mode === 'sequential' && flow.sections.length > 1 ? (
          <div className="flex gap-6">
            <div data-testid="wizard-steps" className=" shrink-0">
              <SectionFlow.Steps />
            </div>
            <div className="min-w-0 flex-1">{children}</div>
          </div>
        ) : (
          children
        )}
      </div>
    </Drawer>
  )
}
